import { useState } from "react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { FieldError, FieldErrors, get, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";

import { Box, CircularProgress, Grid2 as Grid, List, ListItem, ListItemButton, Paper, Typography } from "@mui/material";
import DirectionsBoatIcon from "@mui/icons-material/DirectionsBoat";
import AnchorIcon from "@mui/icons-material/Anchor";
import ListAltIcon from "@mui/icons-material/ListAlt";

import { FormDialog } from "components/dialogs/Dialogs";

import { FormattedDate } from "utils/DateUtils";

import { PushedBargePlanningSchema, PushedBargePlanningSchemaInput, PushedBargePlanningSchemaOutput } from "view/pushingtug/utils/pushingTug.schema";
import { getPreliminaryLinkedCalls, getPushingTugPlanningErrors, PushedBargeVoyage, PushingTugCall, PushingTugVoyage } from "view/pushingtug/utils/PushingTugPlanningUtils";
import { styles } from "view/pushingtug/styling/pushingTugPlanning.styles";

import { LinkPushedBargeCallsToPushingTugDocument } from "__generated__/view/pushingtug/graphql/pushingTug.mutations.generated";

interface PushingTugPlanningPlanPushedBargesDialogProps {
	open: boolean;
	onClose: () => void;
	refetch: () => void;
	selectedPushingBargeVoyages: PushedBargeVoyage[];
	pushingTugVoyage: PushingTugVoyage,
}

const PushingTugPlanningPlanPushedBargesDialog = ({ open, onClose, refetch, selectedPushingBargeVoyages, pushingTugVoyage }: PushingTugPlanningPlanPushedBargesDialogProps) => {

	const [ selectedVoyageIndex, setSelectedVoyageIndex ] = useState(0);

	const [ savePlanning, { loading } ] = useMutation(LinkPushedBargeCallsToPushingTugDocument);

	const { handleSubmit, watch, formState: { defaultValues, errors }, setValue } = useForm<PushedBargePlanningSchemaInput, unknown, PushedBargePlanningSchemaOutput>({
		resolver: zodResolver(PushedBargePlanningSchema),
		defaultValues: async () => ({
			voyages: selectedPushingBargeVoyages.map(voyage => ({
				callLinks: getPreliminaryLinkedCalls(voyage.calls, pushingTugVoyage.calls),
			})),
		}),
	});

	const callLinks = watch(`voyages.${selectedVoyageIndex}.callLinks`);

	const onDragEnd = (result: DropResult) => {
		const { source, destination } = result;

		if (!destination) {
			return;
		}

		if (source.droppableId === destination.droppableId) {
			return;
		}

		const updated = [ ...callLinks ];

		const sourceIndex = parseInt(source.droppableId.replace("row-", ""), 10);
		const destIndex = parseInt(destination.droppableId.replace("row-", ""), 10);

		// Copy object to avoid mutating the original array
		const sourceRow = { ...updated[sourceIndex] };
		const destRow = { ...updated[destIndex] };

		if (destRow.pushedBargeCall) {
			// Destination slot already has a call, swap with the source in this case
			[ sourceRow.pushedBargeCall, destRow.pushedBargeCall ] = [ destRow.pushedBargeCall, sourceRow.pushedBargeCall ];
		} else {
			// Destination slot is empty, move the source
			destRow.pushedBargeCall = sourceRow.pushedBargeCall;
			sourceRow.pushedBargeCall = null;
		}

		updated[sourceIndex] = sourceRow;
		updated[destIndex] = destRow;

		setValue(`voyages.${selectedVoyageIndex}.callLinks`, updated, { shouldValidate: true });
	};

	const save = (data: PushedBargePlanningSchemaOutput) => {
		const links = data.voyages.flatMap(voyage => voyage.callLinks);

		savePlanning({
			variables: {
				linkInputs: links
					.filter((it) => !!it.pushedBargeCall?.id)
					.map(it => ({
						callId: it.pushedBargeCall?.id ?? 0,
						masterCallId: it.pushingTugCall.id,
					})),
			},
		}).then(() => {
			onClose();
			refetch();
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={onClose}
			loading={loading}
			maxWidth="lg"
			form="pushing-tug-planning-plan-pushed-barges-form"
			title={intl.formatMessage({ id: "PLAN_BARGES_ON_A_PUSHING_TUG" })}
			dialogStyling={{ content: { padding: 0 } }}
		>
			{defaultValues === undefined ? (
				<Box sx={styles.loadingBox}>
					<CircularProgress size={24} />
				</Box>
			) : (
				<form onSubmit={handleSubmit(save)} id="pushing-tug-planning-plan-pushed-barges-form" style={{ display: "flex" }}>
					<Box sx={styles.sidebar}>
						<Typography variant="subtitle1" gutterBottom sx={styles.header}>
							<DirectionsBoatIcon fontSize="small" color="primary" />
							{intl.formatMessage({ id: "PUSHED_BARGE_VOYAGES" })}
						</Typography>

						<List>
							{selectedPushingBargeVoyages.map((voyage, index) => (
								<ListItem key={voyage.id} disablePadding>
									<ListItemButton
										selected={selectedVoyageIndex === index}
										onClick={() => setSelectedVoyageIndex(index)}
										sx={styles.voyageItem(
											selectedVoyageIndex === index,
											!!Object.keys(get(errors, `voyages.${index}`) ?? {}).length,
										)}>
										<Box sx={styles.voyageItemContentBox}>
											<Typography fontWeight="bold">{voyage.voyageNumber}</Typography>
											<Box>
												<PushingTugVoyageErrors errors={errors} voyageIndex={index} />
											</Box>
										</Box>

										<Typography variant="body2" color="text.secondary">
											{voyage.ship.name}
										</Typography>

										<Typography variant="caption" color="text.secondary">
											{voyage.from?.description ?? "-"} → {" "}
											{voyage.to?.description ?? "-"}
										</Typography>
									</ListItemButton>
								</ListItem>
							))}
						</List>
					</Box>

					<Box sx={styles.contentBox}>
						<Grid container spacing={2}>
							<Grid size={6}>
								<Typography
									variant="subtitle1"
									gutterBottom
									sx={styles.header}>
									<ListAltIcon fontSize="small" color="primary" />
									{intl.formatMessage({ id: "PUSHED_BARGE_CALLS" })}
								</Typography>
							</Grid>
							<Grid size={6}>
								<Typography
									variant="subtitle1"
									gutterBottom
									sx={styles.header}>
									<AnchorIcon fontSize="small" color="primary" />
									{intl.formatMessage({ id: "PUSHING_TUG_CALLS" })}
								</Typography>
							</Grid>
						</Grid>

						<DragDropContext onDragEnd={onDragEnd}>
							{callLinks.map((row, rowIndex) => (
								<Box sx={styles.callLinkRowBox}>
									<Droppable droppableId={`row-${rowIndex}`} type="BARGE_CALLS">
										{(dropProvided, snapshot) => (
											<Box
												ref={dropProvided.innerRef}
												{...dropProvided.droppableProps}
												sx={styles.droppableZone(snapshot.isDraggingOver)}>
												{row.pushedBargeCall ? (
													<Draggable draggableId={String(row.pushedBargeCall.id)} index={0}>
														{(dragProvided, snapshot) => (
															<Box
																ref={dragProvided.innerRef}
																{...dragProvided.draggableProps}
																{...dragProvided.dragHandleProps}
																sx={{ flex: 1 }}
															>
																<CallInformation
																	title={row.pushedBargeCall!.location.description}
																	call={row.pushedBargeCall!}
																	dragging={snapshot.isDragging}
																	linkError={get(errors, `voyages.${selectedVoyageIndex}.callLinks.${rowIndex}`)}
																/>
															</Box>
														)}
													</Draggable>
												) : (
													<Typography
														variant="body2"
														color="text.secondary"
														fontStyle="italic"
														sx={styles.emptyDroppableText}
													>
														{intl.formatMessage({ id: "DRAG_BARGE_CALL_TO_LINK_IT_TO_TUG_CALL" })}
													</Typography>
												)}
											</Box>
										)}
									</Droppable>

									<CallInformation
										title={row.pushingTugCall.location.description}
										call={row.pushingTugCall}
										isStatic
									/>
								</Box>
							))}
						</DragDropContext>
					</Box>
				</form>
			)}
		</FormDialog>
	);
};

const PushingTugVoyageErrors = ({ errors, voyageIndex }: {
	errors: FieldErrors<PushedBargePlanningSchemaInput>;
	voyageIndex: number;
}) => {
	const linkErrors: Record<number, FieldError> = get(errors, `voyages.${voyageIndex}.callLinks`) ?? {};
	return getPushingTugPlanningErrors(Object.values(linkErrors));
};

interface CallInformationProps {
	title: string;
	call: Pick<PushingTugCall, "pta" | "ptd">;
	dragging?: boolean;
	isStatic?: boolean;
	linkError?: FieldError | null;
}

const CallInformation = ({ title, call, dragging = false, isStatic = false, linkError }: CallInformationProps) => {

	const { pta, ptd } = call;

	return (
		<Paper sx={styles.callInformation(dragging, isStatic)}>
			<Box sx={styles.callInfoTitleBox}>
				<Typography variant="caption" fontWeight="bold" color="text.primary">
					{title}
				</Typography>

				{linkError && getPushingTugPlanningErrors([ linkError ])}
			</Box>

			<Box sx={styles.callInfoDateBox}>
				<Typography variant="caption" color="text.secondary">
					{intl.formatMessage({ id: "CALLS_PTA" })}: {<FormattedDate date={pta} />}
				</Typography>

				<Typography variant="caption" color="text.secondary">
					{intl.formatMessage({ id: "CALLS_PTD" })}: {<FormattedDate date={ptd} />}
				</Typography>
			</Box>
		</Paper>
	);
};

export default PushingTugPlanningPlanPushedBargesDialog;
